import { useQuery, UseQueryResult, keepPreviousData, QueryOptions } from '@tanstack/react-query';

import type { Entity, HelperResult } from '@openclassrooms/sdk-web';

import usePagination from '../hooks/usePagination';
import type Pagination from './Pagination';

export interface UseFindFilters<SearchParams = Record<string, string>> {
  searchQuery?: string;
  searchParams?: SearchParams;
  itemsByPage?: number;
  defaultPage?: number;
  headers?: Record<string, string | number>;
  [others: string]: any;
}

export interface UseFindParams {
  filters?: UseFindFilters;
  [others: string]: any;
}

interface Data<EntityType> {
  entities: EntityType[];
  totalEntities: number;
  totalPages: number;
}

type UseFindReturnType<EntityType> = UseQueryResult<Data<EntityType>> & {
  pagination?: Pagination;
  data: Data<EntityType>;
};

export default function getUseFind<ParamsType extends UseFindParams, EntityType extends Entity>(
  find: (params: ParamsType) => HelperResult<EntityType[]>,
  queryKeyList: string
): (findParams?: ParamsType, queryOptions?: QueryOptions) => UseFindReturnType<EntityType> {
  return (findParams, queryOptions = {}) => {
    const { filters = {}, ...params } = findParams ?? {};
    const {
      searchQuery,
      searchParams = {},
      headers = {},
      itemsByPage = 20,
      defaultPage = 1,
    } = filters;
    const [pagination, currentRange, getTotalPages] = usePagination({ itemsByPage, defaultPage });
    const formattedFilters = {
      headers,
      ...(searchQuery ? { q: searchQuery } : {}),
      ...searchParams,
      ...currentRange,
    };

    const queryResult = useQuery({
      staleTime: 5000,
      ...queryOptions,
      queryKey: [queryKeyList, formattedFilters, params],
      queryFn: async () => {
        const { body: entities, pagination: responsePagination } = await find({
          ...findParams,
          filters: formattedFilters,
        });

        const totalEntities = responsePagination
          ? responsePagination.totalElements
          : entities?.length;

        return {
          entities,
          totalEntities: totalEntities ?? 0,
          totalPages: getTotalPages(totalEntities),
        };
      },
      placeholderData: keepPreviousData,
    });

    return {
      ...queryResult,
      pagination,
    };
  };
}
